import { resolveBillingBlockedRoute, getLatestBillingSubscription } from "./billingNavigation";
import { normalizeLanguage } from "./locale";
import { canAccessSettingsIntegrations, canAccessUpgradeRoute, getCurrentUserRole } from "./userRole";

export const PLAN_IDS = {
  TRIAL: 2,
};

export const parsePlanId = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

export const getLatestBillingPlanId = (payload) => {
  const latestBillingSubscription = getLatestBillingSubscription(payload);
  return parsePlanId(
    latestBillingSubscription?.planId ??
      latestBillingSubscription?.billingPlanId ??
      latestBillingSubscription?.plan?.planId
  );
};

export const getLatestBillingSubscriptionStatus = (payload) =>
  getLatestBillingSubscription(payload)?.status ?? null;

export const isInactiveSubscriptionStatus = (status) => {
  const normalized = String(status || "").toUpperCase();
  return normalized === "INACTIVE" || normalized === "CANCELED" || normalized === "CANCELLED";
};

export const isTrialExpiredByPlanId = (planId, endTrialDate, now = new Date()) => {
  if (planId !== PLAN_IDS.TRIAL) return false;
  if (!endTrialDate || Number.isNaN(endTrialDate.getTime())) return true;
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const boundary = new Date(endTrialDate);
  boundary.setHours(0, 0, 0, 0);
  return boundary < today;
};

/**
 * Derives the billing gate from access-token claims only. The result feeds the
 * post-login route; the app shell re-checks it against the billing summary.
 */
export const evaluateTokenBillingGate = (tokenData = {}, now = new Date()) => {
  const tokenPlanId = getLatestBillingPlanId(tokenData);
  const tokenSubscriptionStatus = getLatestBillingSubscriptionStatus(tokenData);
  const endTrialDate = tokenData?.endTrial ? new Date(tokenData.endTrial) : null;
  const isEndTrialValid = Boolean(endTrialDate) && !Number.isNaN(endTrialDate.getTime());
  const isTrialInFuture = isEndTrialValid && endTrialDate > now;
  const hasActiveSubscription = tokenData?.billingHasActiveSubscription === true;
  const hasActiveSubscriptionFlag = tokenData?.billingHasActiveSubscription != null;

  const shouldUpgrade =
    tokenData?.billingRequiresUpgrade === true ||
    isInactiveSubscriptionStatus(tokenSubscriptionStatus) ||
    isTrialExpiredByPlanId(tokenPlanId, endTrialDate, now) ||
    (!hasActiveSubscription &&
      !isTrialInFuture &&
      (tokenData?.billingTrialExpired === true ||
        (hasActiveSubscriptionFlag && tokenData?.billingCompliant === false) ||
        (isEndTrialValid && !isTrialInFuture)));

  return { shouldUpgrade, tokenPlanId, endTrialDate, isTrialInFuture };
};

export const reconcileShouldUpgradeWithSummary = ({
  currentShouldUpgrade,
  summary,
  tokenPlanId,
  endTrialDate,
  isTrialInFuture,
  now = new Date(),
}) => {
  if (
    !summary ||
    typeof summary !== "object" ||
    Object.keys(summary).length === 0
  ) {
    return currentShouldUpgrade;
  }

  const summaryStatus = summary?.subscription?.status || null;
  const summaryPlanId = parsePlanId(
    summary?.subscription?.planId ??
      summary?.planId ??
      summary?.plan?.planId
  );
  const effectivePlanId = summaryPlanId ?? tokenPlanId;
  const summaryTrialExpiredByPlan = isTrialExpiredByPlanId(
    effectivePlanId,
    endTrialDate,
    now
  );
  const hasActiveFromSummary = String(summaryStatus).toUpperCase() === "ACTIVE";
  const isSummaryInactive = isInactiveSubscriptionStatus(summaryStatus);

  if (isSummaryInactive) return true;
  if (summaryTrialExpiredByPlan) return true;
  if (hasActiveFromSummary) return false;
  if (!isTrialInFuture) return true;
  return false;
};

export const buildNewAccountRoute = (language) =>
  `/new?isLoggedUser=true&lang=${language}`;

export const resolveAccountLandingRoute = ({
  tokenData = {},
  settingsRoute = "/settings",
  dashboardRoute = "/",
}) => {
  const hasAdAccount = [
    tokenData?.linkedinAdAccountId,
    tokenData?.googleAdAccountId,
    tokenData?.metaAdAccountId,
  ].some((id) =>
    typeof id === "string" ? id.trim().length > 0 : typeof id === "number" && id > 0
  );
  if (hasAdAccount) return dashboardRoute;
  return canAccessSettingsIntegrations(getCurrentUserRole(tokenData))
    ? `${settingsRoute}?tab=integrations`
    : settingsRoute;
};

/**
 * Resolves where an already authenticated session lands from its token claims,
 * without waiting for the billing summary.
 */
export const resolveTokenSessionRoute = ({
  tokenData = {},
  settingsRoute = "/settings",
  fallbackLanguage,
  now = new Date(),
}) => {
  const language = normalizeLanguage(tokenData?.language || fallbackLanguage);

  if (tokenData?.requiresNewAccount === true) {
    return {
      route: buildNewAccountRoute(language),
      language,
      requiresNewAccount: true,
      shouldUpgrade: false,
    };
  }

  const { shouldUpgrade } = evaluateTokenBillingGate(tokenData, now);
  const route = shouldUpgrade
    ? resolveBillingBlockedRoute({
        canAccessUpgrade: canAccessUpgradeRoute(getCurrentUserRole(tokenData || {})),
        settingsRoute,
        upgradeRoute: "/upgrade",
        tokenData,
      })
    : resolveAccountLandingRoute({ tokenData, settingsRoute });

  return { route, language, requiresNewAccount: false, shouldUpgrade };
};
